import React, { Component } from 'react';
import DatePicker from 'react-datepicker';

import { getSelectedBranchId } from '../utils/branch';
import { RESERVATION_ORIGIN_LABEL } from '../utils/reservationsApi';
import HeatmapComponent from './HeatmapComponent';

var config = process.env;

// Panel de salón y mesas (ENI-250).
//
// La pregunta que responde es "¿el modelo de mesas cierra?", y el número que la contesta es el
// INGRESO POR SILLA-HORA: entrada más consumo, dividido por las sillas-hora que se ocuparon
// realmente. Es lo único que permite comparar una mesa de 4 con una de 6, o una tarde floja con
// un sábado lleno.
//
// Dos reservas que la pantalla explicita, porque sin ellas los números se leen mal:
//  - la ocupación se promedia sobre los días que TUVIERON actividad, no sobre el rango elegido;
//  - las estadías todavía abiertas no tienen duración medida, así que no entran en el silla-hora.

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`);
const number = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 }));
const percent = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

// 201 min se lee peor que "3 h 21".
const duracion = (min) => {
    if (min == null) return '—';
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    return h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`;
};

const DIAS_LARGOS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

function toIsoDate(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

class SalonAnalyticsComponent extends Component {
    constructor(props) {
        super(props);
        const hoy = new Date();
        const desde = new Date();
        // El canal arrancó el 01/08/2026.
        desde.setDate(desde.getDate() - 30);
        // `metrica` alterna sillas/mesas en el mapa. No es cosmético: llenar sillas es mucho más
        // difícil que llenar mesas —una pareja ocupa una mesa de 6 entera y no se le puede decir
        // que no— y mirar sólo las sillas hace parecer que sobra lugar cuando no sobra.
        this.state = { from: desde, to: hoy, loaded: false, error: null, data: null, metrica: 'sillas' };
    }

    componentDidMount() { this.cargar(); }

    cargar = () => {
        this.setState({ loaded: false, error: null });
        const params = new URLSearchParams({ from: toIsoDate(this.state.from), to: toIsoDate(this.state.to) });
        const branchId = getSelectedBranchId();
        if (branchId) params.set('branchId', branchId);

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/salon?${params.toString()}`, {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                if (body.error > 0) {
                    if (body.error === 1002) { window.location.href = localStorage.getItem('homePageURL'); return; }
                    this.setState({ loaded: true, error: body.message, data: null });
                    return;
                }
                this.setState({ loaded: true, data: body.data, error: null });
            })
            .catch((e) => this.setState({ loaded: true, error: e.message, data: null }));
    };

    render() {
        const { loaded, error, data } = this.state;

        if (!loaded) return <div className="text-center text-muted py-5">Cargando…</div>;
        if (error) return <div className="alert alert-warning">{error}</div>;
        if (!data) return null;

        const { summary, occupancy, tables, funnel, capacity, games } = data;
        const porMesa = this.state.metrica === 'mesas';
        const abiertas = summary.stays - summary.measuredStays;
        const ocupacionMedia = occupancy.slots.length > 0
            ? occupancy.slots.reduce((s, x) => s + (x.occupancyPct || 0), 0) / occupancy.slots.length
            : null;

        return (
            <div>
                <div className="card card-small mb-4">
                    <div className="card-body py-3">
                        <div className="d-flex flex-wrap align-items-end">
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Desde</label>
                                <DatePicker selected={this.state.from} onChange={(d) => this.setState({ from: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Hasta</label>
                                <DatePicker selected={this.state.to} onChange={(d) => this.setState({ to: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mb-2">
                                <button className="btn btn-primary" onClick={this.cargar}>Aplicar</button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── El número que contesta la pregunta, y su contexto ── */}
                <div className="row">
                    {[
                        {
                            t: 'Ingreso por silla-hora', v: money(summary.revenuePerSeatHour),
                            s: `${number(summary.seatHours)} sillas-hora ocupadas`, destacado: true,
                        },
                        {
                            t: 'Ingreso del salón', v: money(summary.totalRevenue),
                            s: `${money(summary.entranceRevenue)} entrada + ${money(summary.consumptionRevenue)} consumo`,
                        },
                        {
                            t: 'Estadía (mediana)', v: duracion(summary.medianMinutes),
                            s: `${number(summary.measuredStays)} de ${number(summary.stays)} estadías cerradas`,
                        },
                        {
                            // ENI-260: el titular es la ESTIMACIÓN, porque el piso subestima casi a
                            // la mitad — el 43% del consumo no se linkea a la mesa al cobrar. El
                            // piso va abajo para que se vea de dónde a dónde va el número.
                            t: 'Gasto por persona (est.)', v: money(summary.estimatedSpendPerPlayer),
                            s: `piso cierto ${money(summary.spendPerPlayer)} · ${number(summary.players)} personas`,
                        },
                    ].map((k) => (
                        <div className="col-lg-3 col-md-6 col-sm-6 mb-4" key={k.t}>
                            <div className={`card card-small h-100 ${k.destacado ? 'border-primary' : ''}`} style={k.destacado ? { borderWidth: 2 } : undefined}>
                                <div className="card-body p-3">
                                    <span className="text-muted d-block" style={{ fontSize: 12 }}>{k.t}</span>
                                    <h5 className="my-1">{k.v}</h5>
                                    <span className="text-muted d-block" style={{ fontSize: 11 }}>{k.s}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Lo que estos números NO dicen ── */}
                <div className="alert alert-light border mb-4" style={{ fontSize: 13 }}>
                    <strong className="d-block mb-1">Cómo leer estos números</strong>
                    <div>
                        • La ocupación se promedia sobre los {number(occupancy.activeDays)} días que tuvieron
                        actividad, no sobre los {number(data.range.days)} del filtro. Dividir por el rango haría
                        parecer vacío un salón lleno.
                    </div>
                    {abiertas > 0 && (
                        <div>
                            • {number(abiertas)} {abiertas === 1 ? 'estadía sigue abierta' : 'estadías siguen abiertas'}:
                            sin hora de salida no hay duración, así que {abiertas === 1 ? 'no entra' : 'no entran'} en
                            el ingreso por silla-hora y {abiertas === 1 ? 'ocupa' : 'ocupan'} una sola franja.
                        </div>
                    )}
                    {data.consumption.unlinkedPct > 0 && (
                        <div>
                            • El <strong>gasto por persona</strong> está estimado repartiendo TODO el consumo de
                            cafetería de los días con mesas ocupadas: {percent(data.consumption.unlinkedPct)} de
                            ese consumo ({number(data.consumption.unlinkedSales)} ventas) no quedó asociado a una
                            mesa al cobrar. El piso cierto —sólo lo asociado— es{' '}
                            {money(summary.spendPerPlayer)}.
                        </div>
                    )}
                    {games.staysWithGame === 0 && games.stays > 0 && (
                        <div>
                            • Ninguna de las {number(games.stays)} estadías tiene registrado qué juego se llevó a la
                            mesa, así que no hay ranking de juegos. Es dato que hoy no se está capturando.
                        </div>
                    )}
                </div>

                {/* ── Ocupación: mapa de calor día × hora ── */}
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom d-flex justify-content-between align-items-start flex-wrap">
                        <div>
                            <h6 className="m-0">Ocupación por día y hora</h6>
                            <span className="text-muted" style={{ fontSize: 12 }}>
                                {porMesa
                                    ? <>% de las {number(capacity.tables)} mesas ocupadas, sin importar cuánta gente había en cada una.</>
                                    : <>% de las {number(capacity.seats)} sillas ocupadas.</>}
                                {' '}Cada fila se promedia sobre las veces que ESE día cayó en el rango.
                            </span>
                        </div>
                        <div className="btn-group btn-group-sm mt-1">
                            {[['sillas', 'Por personas'], ['mesas', 'Por mesa']].map(([k, t]) => (
                                <button
                                    key={k}
                                    className={`btn ${this.state.metrica === k ? 'btn-primary' : 'btn-white'}`}
                                    onClick={() => this.setState({ metrica: k })}
                                >{t}</button>
                            ))}
                        </div>
                    </div>
                    <div className="card-body">
                        {/* Las dos vistas cuentan historias distintas y la brecha ES el dato: con
                            datos reales, el sábado a las 20h da 51% de sillas y 78% de mesas. El
                            51% se lee como "sobra lugar" y no sobra: quedan dos mesas. */}
                        <HeatmapComponent
                            color={porMesa ? '0, 123, 255' : '23, 198, 113'}
                            hideEmptyRows
                            formatCell={(v) => (v > 0 ? `${Math.round(v)}%` : '')}
                            emptyText="Sin estadías en el período."
                            cells={occupancy.matrix.flatMap((fila) => fila.cells.map((c) => ({
                                weekday: fila.weekday,
                                hour: c.hour,
                                value: (porMesa ? c.tablesPct : c.occupancyPct) || 0,
                                detail: fila.openDays === 0
                                    ? 'sin datos para ese día'
                                    : `${DIAS_LARGOS[fila.weekday]} ${c.hour}:00 — `
                                      + (porMesa
                                          ? `${percent(c.tablesPct)}: ${(c.tables || 0).toFixed(1)} de ${number(capacity.tables)} mesas`
                                          : `${percent(c.occupancyPct)}: ${(c.seats || 0).toFixed(1)} de ${number(capacity.seats)} sillas`)
                                      + ` (promedio de ${number(fila.openDays)} día(s))`,
                            })))}
                        />
                        <p className="text-muted mb-0 mt-2" style={{ fontSize: 12 }}>
                            {porMesa
                                ? 'Una mesa cuenta como ocupada aunque tenga una sola persona. Comparalo con "Por personas": la brecha entre las dos es cuánto lugar queda realmente sin vender.'
                                : 'Ojo: una pareja en una mesa de 6 deja la mesa llena y las sillas vacías. Mirá "Por mesa" para saber si de verdad quedaba lugar.'}
                        </p>
                    </div>
                </div>

                <div className="row">
                    {/* ── Rendimiento por mesa ── */}
                    <div className="col-lg-7 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Rendimiento por mesa</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    El ingreso por silla-hora es lo que permite comparar mesas de distinto tamaño.
                                </span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Mesa</th>
                                            <th className="border-0 text-right">Sillas</th>
                                            <th className="border-0 text-right">Usos</th>
                                            <th className="border-0 text-right">Personas</th>
                                            <th className="border-0 text-right">Estadía</th>
                                            <th className="border-0 text-right">Ingreso</th>
                                            <th className="border-0 text-right">Por silla-hora</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tables.length === 0 && (
                                            <tr><td colSpan="7" className="text-center text-muted py-3">Sin estadías en el período.</td></tr>
                                        )}
                                        {tables.map((m) => (
                                            <tr key={m.tableId == null ? 'sin-mesa' : m.tableId}>
                                                <td>{m.label}</td>
                                                <td className="text-right text-muted">{number(m.capacity)}</td>
                                                <td className="text-right">{number(m.stays)}</td>
                                                <td className="text-right">{number(m.players)}</td>
                                                <td className="text-right">{duracion(m.medianMinutes)}</td>
                                                <td className="text-right">{money(m.revenue)}</td>
                                                <td className="text-right font-weight-bold">{money(m.revenuePerSeatHour)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── De dónde viene la gente ── */}
                    <div className="col-lg-5 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">De dónde viene la gente</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Cada mesa del período según de dónde salió. Las cinco filas suman el total.
                                </span>
                            </div>
                            <div className="card-body p-0">
                                {/* La grilla anterior ponía esto y el embudo de reservas en la MISMA
                                    lista, con "se sentaron: 50" sangrado bajo "Agendadas: 3" — como si
                                    50 fuera parte de 3. Son dos preguntas y ahora son dos bloques. */}
                                {/* FR-077 (a) — un bucket por `origin`, no "todo lo que no es
                                    walk-in vino con reserva". Desde que existen la mesa abierta
                                    y la jornada del club hay CUATRO formas de ocupar una mesa, y
                                    la resta las metía a todas en "venían con reserva".

                                    El denominador es `funnel.total` y no `funnel.seated`: los
                                    cinco buckets cuentan filas del período (los cinco suman
                                    `total`), así que es el único porcentaje que cierra en 100.
                                    Es un cambio de denominador sobre un número ya publicado —
                                    las dos filas viejas se leían sobre las sentadas—, por eso el
                                    total del bloque queda explícito debajo y las estadías se
                                    muestran aparte: son otra pregunta. */}
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        {[
                                            [RESERVATION_ORIGIN_LABEL.reserva, funnel.booked],
                                            [RESERVATION_ORIGIN_LABEL.walkin, funnel.walkin],
                                            [RESERVATION_ORIGIN_LABEL.actividad, funnel.actividad],
                                            [RESERVATION_ORIGIN_LABEL.abierta, funnel.abierta],
                                            // Filas anteriores a que `origin` existiera (y el split de
                                            // una mesa abierta, que nace sin origen). Se declaran en vez
                                            // de repartirse entre los otros, para que la suma dé el total.
                                            ['Sin origen', funnel.sinOrigen],
                                        ].map(([titulo, valor]) => (
                                            <tr key={titulo}>
                                                <td>{titulo}</td>
                                                <td className="text-right font-weight-bold">{number(valor)}</td>
                                                <td className="text-right text-muted" style={{ fontSize: 12, width: 60 }}>
                                                    {valor != null && funnel.total > 0 ? percent((Number(valor) / funnel.total) * 100) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="bg-light">
                                            <td className="font-weight-bold">Total de mesas</td>
                                            <td className="text-right font-weight-bold">{number(funnel.total)}</td>
                                            <td />
                                        </tr>
                                        <tr>
                                            <td className="text-muted">
                                                Total de estadías
                                                <span className="text-muted"> (las que llegaron a sentarse)</span>
                                            </td>
                                            <td className="text-right text-muted">{number(funnel.seated)}</td>
                                            <td className="text-right text-muted" style={{ fontSize: 12 }}>
                                                {funnel.total > 0 ? percent((funnel.seated / funnel.total) * 100) : '—'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── Las reservas agendadas: su propio embudo ── */}
                    <div className="col-lg-7 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Reservas agendadas</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Sólo las que se agendaron: qué pasó con ellas y con cuánta anticipación se piden.
                                </span>
                            </div>
                            <div className="card-body">
                                <div className="row text-center mb-3">
                                    {[
                                        { t: 'Agendadas', v: number(data.reservas.booked) },
                                        { t: 'Se sentaron', v: number(data.reservas.seated) },
                                        { t: 'Canceladas', v: number(data.reservas.cancelled) },
                                        {
                                            t: 'No vinieron', v: number(data.reservas.noShow),
                                            malo: data.reservas.noShow > 0,
                                            pie: data.reservas.noShowPct == null ? null : percent(data.reservas.noShowPct),
                                        },
                                    ].map((k) => (
                                        <div className="col-3" key={k.t}>
                                            <h5 className={`mb-0 ${k.malo ? 'text-danger' : ''}`}>{k.v}</h5>
                                            <span className="text-muted d-block" style={{ fontSize: 11 }}>{k.t}</span>
                                            {k.pie && <span className="text-muted d-block" style={{ fontSize: 10 }}>{k.pie}</span>}
                                        </div>
                                    ))}
                                </div>

                                <div className="border-top pt-3">
                                    <span className="text-muted d-block mb-1" style={{ fontSize: 12 }}>
                                        Con cuánta anticipación reservan
                                        {data.reservas.leadTimeAvg != null && <> — promedio {number(Math.round(data.reservas.leadTimeAvg))} día(s)</>}
                                    </span>
                                    {data.reservas.leadTimeDistribution.length === 0
                                        ? <span className="text-muted" style={{ fontSize: 12 }}>Sin reservas agendadas en el período.</span>
                                        : (
                                            <div className="d-flex flex-wrap">
                                                {/* La distribución y no sólo el promedio: con pocas reservas, "1 día
                                                    de promedio" esconde que la mitad reserva el mismo día y la otra
                                                    mitad con una semana. */}
                                                {data.reservas.leadTimeDistribution.map((d) => (
                                                    <div key={d.days} className="mr-3 mb-1" style={{ fontSize: 12 }}>
                                                        <strong>{d.days === 0 ? 'mismo día' : `${d.days} día${d.days > 1 ? 's' : ''} antes`}</strong>
                                                        <span className="text-muted">: {number(d.count)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* ── Lo que viene ── */}
                    <div className="col-lg-7 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Reservas por venir</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    {/* Esto NO se acota al rango del filtro: la pregunta es "qué tengo por
                                        delante", no "qué pasó en el período que estoy mirando". */}
                                    Hacia adelante desde hoy, sin importar el rango elegido.{' '}
                                    {data.reservas.upcomingList.length > 0 && (
                                        <><strong>{number(data.reservas.upcomingPlayers)}</strong> persona(s) comprometida(s).</>
                                    )}
                                </span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Día</th>
                                            <th className="border-0">Hora</th>
                                            <th className="border-0">Nombre</th>
                                            <th className="border-0">Mesa</th>
                                            <th className="border-0 text-right">Personas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.reservas.upcomingList.length === 0 && (
                                            <tr><td colSpan="5" className="text-center text-muted py-3">No hay reservas agendadas hacia adelante.</td></tr>
                                        )}
                                        {data.reservas.upcomingList.map((r, i) => (
                                            <tr key={`${r.date}-${r.startHour}-${i}`}>
                                                <td>
                                                    {r.date}
                                                    <span className="text-muted ml-2" style={{ fontSize: 11 }}>
                                                        {r.inDays === 0 ? 'hoy' : `en ${r.inDays} día${r.inDays > 1 ? 's' : ''}`}
                                                    </span>
                                                </td>
                                                <td>{r.startHour}:00</td>
                                                <td>{r.name || <span className="text-muted">—</span>}</td>
                                                <td className="text-muted">{r.table || '—'}</td>
                                                <td className="text-right font-weight-bold">{number(r.players)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── Mesas bloqueadas ── */}
                    <div className="col-lg-5 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Mesas bloqueadas</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    {/* Sin esto, una hora bloqueada se lee como una hora sin demanda. */}
                                    Franjas que no se pudieron vender (torneos, eventos). Una hora bloqueada
                                    aparece vacía en el mapa de calor y no lo estaba.
                                </span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        {data.bloqueos.items.length === 0 && (
                                            <tr><td className="text-center text-muted py-3">Sin bloqueos en el período.</td></tr>
                                        )}
                                        {data.bloqueos.items.map((b, i) => (
                                            <tr key={i}>
                                                <td style={{ fontSize: 12 }}>
                                                    {b.date}
                                                    <span className="text-muted ml-2">{b.startHour}–{b.endHour}h</span>
                                                </td>
                                                <td style={{ fontSize: 12 }}>
                                                    {b.wholeVenue
                                                        ? <span className="badge badge-danger" style={{ fontSize: 9 }}>todo el salón</span>
                                                        : b.table}
                                                </td>
                                                <td className="text-muted" style={{ fontSize: 11 }}>{b.description || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default SalonAnalyticsComponent;
